/**
 * career-extraction-review — the onboarding review surface (route `/onboarding/review`).
 *
 * Graded claim cards (grade + status legible as registered `is-*` classes),
 * confirm / edit / discard as `data-act` verbs matching the api verbs (optimistic
 * via the evidence store — ABSOLUTE end-state, the double-count trap is probed in a
 * store test); CV-draft + profile-seed panels; and the presented German
 * Kandidatenprofil with provenance chips + a `Stand: v<n>` staleness cue, plus
 * re-derive + re-author affordances.
 *
 * The Evidence stream is subscribed on attach: its replay hydrates the store, live
 * claim.* ticks (from confirm/edit/discard) reconcile through the ONE apply path.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { EvidenceStoreToken, I18nToken, PushToken, SessionToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import { careerStream, type EvidenceClaimView, type EvidenceStore, type ShellSession } from "../domain"
import { renderClaimCard } from "../lib/claim-card"
import { authoredOnDate, narrativeBlocks } from "../lib/narrative"

interface CvSummary {
  name: string
  label: string
  workCount: number
  educationCount: number
  skillCount: number
  isDraft: boolean
}
interface ProfileSummary {
  throughline: string
  motivationCount: number
  valueCount: number
}
interface NarrativeView {
  body: string
  provenanceCount: number
  promptRef: string
  /** Honest staleness (M4): true iff the confirmed ledger changed after authoring. */
  stale: boolean
  /** Localised authored-on date, or null. */
  authoredOn: string | null
}

export class ReviewVm implements ViewModel {
  /** One id-sorted list so a claim keeps its place across confirm (N5) — status is a class, not a move. */
  readonly claims: ReadonlySignal<readonly EvidenceClaimView[]>
  readonly open: ReadonlySignal<readonly EvidenceClaimView[]>
  readonly confirmed: ReadonlySignal<readonly EvidenceClaimView[]>
  readonly cv: Signal<CvSummary | null> = signal<CvSummary | null>(null)
  readonly profile: Signal<ProfileSummary | null> = signal<ProfileSummary | null>(null)
  readonly narrative: Signal<NarrativeView | null> = signal<NarrativeView | null>(null)
  readonly editingId: Signal<string | null> = signal<string | null>(null)
  readonly authoring: Signal<boolean> = signal(false)
  readonly rederiving: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** True once a completed ingest run exists (proposed/confirmed) — distinguishes
   *  "no run yet" from "run completed, zero statements extracted" (B1 honest empty). */
  readonly runCompleted: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  private ingestId: string | null = null

  constructor(
    private readonly store: EvidenceStore,
    private readonly push: PushChannel,
    private readonly session: ShellSession,
    i18n: CareerI18n,
  ) {
    this.t = i18n.t
    this.claims = store.list("alle")
    this.open = store.list("offen")
    this.confirmed = store.list("bestaetigt")
    this.renderDeps = [this.claims, this.open, this.confirmed, this.cv, this.profile, this.narrative, this.editingId, this.authoring, this.rederiving, this.error, this.runCompleted, store.pending]
  }

  attach(): () => void {
    const userId = this.session.user?.sub
    if (userId) this.push.subscribe([careerStream(userId, "Evidence")])
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    try {
      const [cv, prof, ingest] = await Promise.all([
        apiGet<{ cv: Record<string, any> | null; isDraft: boolean }>("/api/cv"),
        apiGet<{ profile: Record<string, any> | null; narrative: Record<string, any> | null; narrativeStale: boolean; narrativeAuthoredAt: string | null }>("/api/profile"),
        apiGet<Array<{ id: string; status: string }>>("/api/ingest"),
      ])
      this.cv.set(cv.cv ? summariseCv(cv.cv, cv.isDraft) : null)
      this.profile.set(prof.profile ? summariseProfile(prof.profile) : null)
      this.narrative.set(prof.narrative ? toNarrativeView(prof.narrative, prof.narrativeStale, prof.narrativeAuthoredAt) : null)
      const proposed = ingest.find((r) => r.status === "proposed") ?? ingest[ingest.length - 1]
      this.ingestId = proposed ? proposed.id.replace(/^urn:career:ingest:/, "") : null
      this.runCompleted.set(ingest.some((r) => r.status === "proposed" || r.status === "confirmed"))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }

  confirmClaim(claimId: string): void {
    void this.store.command({ type: "confirm-claim", payload: { claimId } })
  }
  discardClaim(claimId: string): void {
    void this.store.command({ type: "discard-claim", payload: { claimId } })
  }
  toggleEdit(claimId: string): void {
    this.editingId.set(this.editingId.value === claimId ? null : claimId)
  }

  async saveEdit(claimId: string, statement: string): Promise<void> {
    const claim = this.store.entity(claimId as never).value
    if (!claim || statement.trim().length === 0) return
    this.editingId.set(null)
    try {
      // Statement edit preserves the existing sources/tags; the server re-derives the grade.
      await apiPost("/api/evidence/edit", {
        claimId,
        claim: { statement, grade: claim.grade, origin: claim.origin, sources: claim.sources, tags: claim.tags },
      })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }

  async authorNarrative(): Promise<void> {
    this.authoring.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/profile/narrative")
      const prof = await apiGet<{ narrative: Record<string, any> | null; narrativeStale: boolean; narrativeAuthoredAt: string | null }>("/api/profile")
      this.narrative.set(prof.narrative ? toNarrativeView(prof.narrative, prof.narrativeStale, prof.narrativeAuthoredAt) : null)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.authoring.set(false)
    }
  }

  async rederive(): Promise<void> {
    this.rederiving.set(true)
    this.error.set(null)
    try {
      await apiPost("/api/ingest/rederive")
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.rederiving.set(false)
    }
  }

  async confirmIngest(): Promise<void> {
    if (!this.ingestId) return
    const confirmedIds = this.confirmed.value.map((c) => c.id)
    try {
      await apiPost("/api/ingest/confirm", { ingestId: this.ingestId, confirmedClaims: confirmedIds })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }
}

export class CareerExtractionReview extends KitElement<ReviewVm> {
  protected createViewModel(ctx: KitContext): ReviewVm {
    return new ReviewVm(ctx.inject(EvidenceStoreToken), ctx.inject(PushToken), ctx.inject(SessionToken), ctx.inject(I18nToken))
  }

  private claimCard(vm: ReviewVm, claim: EvidenceClaimView): TemplateResult {
    return renderClaimCard(claim, { editing: vm.editingId.value === claim.id, t: vm.t })
  }

  protected render(vm: ReviewVm): TemplateResult {
    const claims = vm.claims.value
    const open = vm.open.value
    const confirmed = vm.confirmed.value
    const cv = vm.cv.value
    const profile = vm.profile.value
    const narrative = vm.narrative.value
    const err = vm.error.value
    // Honest empty state (B1): after a completed run, a zero-claim ledger is NOT "start first".
    const emptyKey: CareerKey = vm.runCompleted.value ? "review.claims.emptyAfterRun" : "review.claims.empty"
    return html`
      <section class="review surface" aria-labelledby="review-title">
        <h1 id="review-title" class="surface-title" lang="de">${vm.t("review.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("review.lead")}</p>
        ${err ? html`<p class="review-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        <div class="review-grid">
          <section class="review-claims" aria-labelledby="review-claims-title">
            <h2 id="review-claims-title" class="review-section-title" lang="de">
              ${vm.t("review.claims.title")}
              <span class="chip" lang="de">${vm.t("review.claims.count", { open: open.length, confirmed: confirmed.length })}</span>
            </h2>
            ${claims.length === 0
              ? html`<p class="review-empty" lang="de">${vm.t(emptyKey)}</p>`
              : html`<ol class="claim-list" role="list">
                  ${repeat(claims, (c) => c.urn, (c) => this.claimCard(vm, c))}
                </ol>`}
            <div class="review-claims-actions">
              <button data-act="confirm-ingest" ?disabled=${confirmed.length === 0} lang="de">${vm.t("review.confirmIngest")}</button>
              <button data-act="rederive" class=${classMap({ "is-pending": vm.rederiving.value })} ?disabled=${vm.rederiving.value} lang="de">
                ${vm.rederiving.value ? vm.t("review.rederiving") : vm.t("review.rederive")}
              </button>
            </div>
          </section>

          <aside class="review-panels">
            <section class="review-panel review-cv" aria-labelledby="review-cv-title">
              <h2 id="review-cv-title" class="review-section-title" lang="de">${vm.t("review.cv.title")}</h2>
              ${cv
                ? html`<dl class="review-facts">
                    <dt lang="de">${vm.t("review.cv.name")}</dt><dd lang="de">${cv.name}</dd>
                    <dt lang="de">${vm.t("review.cv.stations")}</dt><dd lang="de">${vm.t("review.cv.stationsN", { n: cv.workCount })}</dd>
                    <dt lang="de">${vm.t("review.cv.skills")}</dt><dd lang="de">${vm.t("review.cv.skillsN", { n: cv.skillCount })}</dd>
                  </dl>`
                : html`<p class="review-empty" lang="de">${vm.t("review.cv.empty")}</p>`}
            </section>

            <section class="review-panel review-profile" aria-labelledby="review-profile-title">
              <h2 id="review-profile-title" class="review-section-title" lang="de">${vm.t("review.profile.title")}</h2>
              ${profile
                ? html`<p class="review-throughline" lang="de">${profile.throughline || vm.t("review.profile.noThroughline")}</p>
                    <p class="review-facet-counts">
                      <span class="chip" lang="de">${vm.t("review.profile.motives", { count: profile.motivationCount, m: profile.motivationCount })}</span>
                      <span class="chip" lang="de">${vm.t("review.profile.values", { count: profile.valueCount, v: profile.valueCount })}</span>
                    </p>`
                : html`<p class="review-empty" lang="de">${vm.t("review.profile.empty")}</p>`}
            </section>

            <section class="review-panel review-narrative" aria-labelledby="review-narrative-title" aria-live="polite">
              <h2 id="review-narrative-title" class="review-section-title" lang="de">${vm.t("review.narrative.title")}</h2>
              ${narrative
                ? html`
                    <div class="narrative-body">
                      ${narrativeBlocks(narrative.body).map((b) =>
                        b.heading ? html`<h3 lang="de">${b.text}</h3>` : html`<p lang="de">${b.text}</p>`,
                      )}
                    </div>
                    <p class="narrative-meta">
                      <span class="narrative-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: narrative.provenanceCount })}</span>
                      ${narrative.stale
                        ? html`<span class="narrative-cue chip is-stale" lang="de">${vm.t("review.narrative.stale")}</span>`
                        : narrative.authoredOn
                          ? html`<span class="narrative-cue chip" lang="de">${vm.t("review.narrative.authoredOn", { date: narrative.authoredOn })}</span>`
                          : nothing}
                    </p>
                    <button data-act="author-narrative" class=${classMap({ "is-pending": vm.authoring.value })} ?disabled=${vm.authoring.value} lang="de">
                      ${vm.authoring.value ? vm.t("review.narrative.authoring") : vm.t("review.narrative.reauthor")}
                    </button>
                  `
                : html`
                    <p class="review-empty" lang="de">${vm.t("review.narrative.empty")}</p>
                    <button data-act="author-narrative" class=${classMap({ "is-pending": vm.authoring.value })} ?disabled=${vm.authoring.value || confirmed.length === 0} lang="de">
                      ${vm.authoring.value ? vm.t("review.narrative.authoring") : vm.t("review.narrative.author")}
                    </button>
                  `}
            </section>
          </aside>
        </div>
      </section>
    `
  }

  protected override actions(vm: ReviewVm): ActionMap {
    return {
      "confirm-claim": ({ claimId }) => { if (claimId) vm.confirmClaim(claimId) },
      "discard-claim": ({ claimId }) => { if (claimId) vm.discardClaim(claimId) },
      "edit-toggle": ({ claimId }) => { if (claimId) vm.toggleEdit(claimId) },
      "edit-claim": ({ claimId }, event) => {
        if (!claimId) return
        const card = (event.target as HTMLElement).closest(".claim-card")
        const input = card?.querySelector<HTMLInputElement>("input.claim-edit-input")
        if (input) void vm.saveEdit(claimId, input.value)
      },
      "confirm-ingest": () => void vm.confirmIngest(),
      rederive: () => void vm.rederive(),
      "author-narrative": () => void vm.authorNarrative(),
    }
  }
}

// ─── projections from the REST payloads ──────────────────────────────────────

function summariseCv(cv: Record<string, any>, isDraft: boolean): CvSummary {
  return {
    name: String(cv.basics?.name ?? ""),
    label: String(cv.basics?.label ?? ""),
    workCount: Array.isArray(cv.work) ? cv.work.length : 0,
    educationCount: Array.isArray(cv.education) ? cv.education.length : 0,
    skillCount: Array.isArray(cv.skills) ? cv.skills.length : 0,
    isDraft,
  }
}
function summariseProfile(p: Record<string, any>): ProfileSummary {
  return {
    throughline: String(p.identity?.throughline ?? ""),
    motivationCount: Array.isArray(p.motivations) ? p.motivations.length : 0,
    valueCount: Array.isArray(p.values) ? p.values.length : 0,
  }
}
function toNarrativeView(n: Record<string, any>, stale: boolean, authoredAt: string | null): NarrativeView {
  const authoredOn = authoredOnDate(authoredAt)
  return {
    body: String(n.body ?? ""),
    provenanceCount: Array.isArray(n.provenance) ? n.provenance.length : 0,
    promptRef: String(n.meta?.promptRef ?? ""),
    stale,
    authoredOn,
  }
}
